import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { CG_DISTRICTS, CG_DISTRICT_LIST } from "@/lib/cgLocations";
import { MapPin, ArrowLeft, Newspaper } from "lucide-react";

function relTime(iso) { return iso ? new Date(iso).toLocaleDateString("hi-IN") : ""; }

export function CGStatePage() {
  return (
    <>
      <PublicHeader/>
      <section className="bg-gradient-to-br from-emerald-800 via-green-800 to-teal-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-14">
          <div className="flex items-center gap-3 mb-3">
            <MapPin className="w-5 h-5 text-amber-300"/>
            <span className="uppercase tracking-[0.28em] text-[11px] text-amber-300 font-black">Chhattisgarh · छत्तीसगढ़</span>
          </div>
          <h1 className="font-devnagri-heading text-4xl md:text-5xl font-black">हर जिले, <span className="text-amber-300">हर ब्लॉक की ख़बर</span></h1>
          <p className="text-emerald-100 mt-3">33 जिले · 250+ ब्लॉक — अपना क्षेत्र चुनिए, ज़मीनी ख़बरें पाइये</p>
        </div>
      </section>
      <main className="max-w-6xl mx-auto px-4 py-10" data-testid="cg-state-page">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {CG_DISTRICT_LIST.map((d) => (
            <Link key={d} to={`/cg/${encodeURIComponent(d)}`}
              className="group bg-white border border-slate-200 rounded-lg p-4 hover:border-emerald-500 hover:shadow-md transition"
              data-testid={`dist-${d}`}>
              <MapPin className="w-4 h-4 text-emerald-600 mb-2"/>
              <div className="font-bold text-[#0F172A] group-hover:text-emerald-700 text-sm leading-tight">{d}</div>
              <div className="text-[10px] text-slate-500 mt-1">{CG_DISTRICTS[d].length} ब्लॉक</div>
            </Link>
          ))}
        </div>
      </main>
      <PublicFooter/>
    </>
  );
}

export function CGDistrictPage() {
  const { district } = useParams();
  const d = decodeURIComponent(district);
  const blocks = CG_DISTRICTS[d] || [];
  const [news, setNews] = useState([]);
  useEffect(() => {
    api.get("/news", { params: { district: d, limit: 40 } }).then((r) => setNews(r.data)).catch(() => {});
  }, [d]);
  return (
    <>
      <PublicHeader/>
      <section className="bg-gradient-to-br from-emerald-800 to-teal-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <Link to="/cg" className="inline-flex items-center gap-1.5 text-emerald-200 text-sm mb-2 hover:text-white"><ArrowLeft className="w-4 h-4"/>छत्तीसगढ़</Link>
          <h1 className="font-devnagri-heading text-3xl md:text-4xl font-black flex items-center gap-2"><MapPin className="w-6 h-6 text-amber-300"/>{d}</h1>
        </div>
      </section>
      <main className="max-w-6xl mx-auto px-4 py-8" data-testid="cg-district-page">
        <h3 className="font-bold text-[#0F172A] mb-3">ब्लॉक चुनें</h3>
        <div className="flex flex-wrap gap-2 mb-8">
          {blocks.map((b) => (
            <Link key={b} to={`/cg/${encodeURIComponent(d)}/${encodeURIComponent(b)}`}
              className="px-4 py-1.5 rounded-full bg-white border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 text-sm text-[#0F172A]"
              data-testid={`blk-${b}`}>{b}</Link>
          ))}
        </div>
        <h3 className="font-bold text-[#0F172A] mb-3 flex items-center gap-2"><Newspaper className="w-4 h-4 text-[#EA580C]"/>{d} की सभी ख़बरें ({news.length})</h3>
        <NewsGrid items={news}/>
      </main>
      <PublicFooter/>
    </>
  );
}

export function CGBlockPage() {
  const { district, block } = useParams();
  const d = decodeURIComponent(district);
  const b = decodeURIComponent(block);
  const [news, setNews] = useState([]);
  useEffect(() => {
    api.get("/news", { params: { district: d, block: b, limit: 40 } }).then((r) => setNews(r.data)).catch(() => {});
  }, [d, b]);
  return (
    <>
      <PublicHeader/>
      <section className="bg-gradient-to-br from-emerald-800 to-teal-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <Link to={`/cg/${encodeURIComponent(d)}`} className="inline-flex items-center gap-1.5 text-emerald-200 text-sm mb-2 hover:text-white"><ArrowLeft className="w-4 h-4"/>{d}</Link>
          <h1 className="font-devnagri-heading text-3xl md:text-4xl font-black flex items-center gap-2"><MapPin className="w-6 h-6 text-amber-300"/>{b}</h1>
          <p className="text-emerald-100 text-sm mt-1">जिला: {d}</p>
        </div>
      </section>
      <main className="max-w-6xl mx-auto px-4 py-8" data-testid="cg-block-page">
        <h3 className="font-bold text-[#0F172A] mb-3">{b} की ख़बरें ({news.length})</h3>
        <NewsGrid items={news}/>
      </main>
      <PublicFooter/>
    </>
  );
}

function NewsGrid({ items }) {
  if (items.length === 0) return <div className="text-slate-500 italic py-12 text-center bg-white rounded-lg border border-dashed border-slate-200">इस क्षेत्र से अभी कोई ख़बर नहीं। जल्द ही जोड़ी जायेंगी।</div>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((n) => (
        <Link key={n.id} to={`/article/${n.slug}`} className="group bg-white rounded-lg border border-slate-100 overflow-hidden hover:shadow-md transition" data-testid={`ng-${n.id}`}>
          {n.image_url && <div className="aspect-video bg-slate-100"><img src={n.image_url} alt="" className="w-full h-full object-cover"/></div>}
          <div className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-[#EA580C] font-bold">{n.category}</div>
            <h3 className="font-devnagri-heading font-bold text-[#0F172A] group-hover:text-[#DC2626] line-clamp-3 leading-snug mt-1">{n.title}</h3>
            <div className="text-[10px] text-slate-400 mt-2">{relTime(n.created_at)} · {n.author}</div>
          </div>
        </Link>
      ))}
    </div>
  );
}
